import { forwardRef, useEffect, useRef } from "react";
import type { TextareaHTMLAttributes } from "react";

// Every free-text field in the app is a textarea that grows with its content
// up to MAX_LINES, then scrolls internally instead of growing further.
const MAX_LINES = 6;

function lineMetrics(el: HTMLTextAreaElement) {
  const style = window.getComputedStyle(el);
  const lineHeight = parseFloat(style.lineHeight) || 20;
  const vertical =
    (parseFloat(style.paddingTop) || 0) +
    (parseFloat(style.paddingBottom) || 0) +
    (parseFloat(style.borderTopWidth) || 0) +
    (parseFloat(style.borderBottomWidth) || 0);
  return { lineHeight, vertical };
}

function resize(el: HTMLTextAreaElement, minLines: number): void {
  const { lineHeight, vertical } = lineMetrics(el);
  el.style.height = "auto";
  const contentHeight = Math.max(el.scrollHeight, lineHeight * minLines + vertical);
  const maxHeight = lineHeight * MAX_LINES + vertical;
  el.style.height = `${Math.min(contentHeight, maxHeight)}px`;
  el.style.overflowY = contentHeight > maxHeight ? "auto" : "hidden";
}

export interface AutoGrowTextareaProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "rows"> {
  /**
   * Minimum visible lines before the field starts growing (default 2). A
   * single visible line reads as a plain single-line input at rest — every
   * field in the app must look multiline even when empty or short.
   */
  readonly minLines?: number;
}

/** A textarea that grows with its content up to 6 lines, then scrolls. */
export const AutoGrowTextarea = forwardRef<HTMLTextAreaElement, AutoGrowTextareaProps>(
  function AutoGrowTextarea({ value, onChange, minLines = 2, style, ...rest }, forwardedRef) {
    const innerRef = useRef<HTMLTextAreaElement | null>(null);

    useEffect(() => {
      if (innerRef.current) resize(innerRef.current, minLines);
    }, [value, minLines]);

    useEffect(() => {
      const el = innerRef.current;
      if (!el) return undefined;
      const onResize = () => resize(el, minLines);
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    }, [minLines]);

    return (
      <textarea
        ref={(node) => {
          innerRef.current = node;
          if (typeof forwardedRef === "function") forwardedRef(node);
          else if (forwardedRef) forwardedRef.current = node;
        }}
        value={value}
        onChange={(e) => {
          resize(e.currentTarget, minLines);
          onChange?.(e);
        }}
        rows={1}
        style={{ resize: "none", overflowY: "hidden", ...style }}
        {...rest}
      />
    );
  },
);
